import type {
	ContentQueryResultItem,
	DigitalEditionItemData,
	Item,
	ListItemData,
	Page,
	PlaylistItem,
	PlaylistResponse,
	ReelData
} from '../../types/types.ts';
import {LoadResourceError} from '../lib/errors.ts';
import type {LayoutParser} from '../lib/layout-parser.ts';
import type {Store} from '../lib/store.ts';
import type {Article, RelatedArticle} from '../models/article.ts';
import type {ContentSourceEdition} from '../models/content-source-edition.ts';
import type {ReaderItem} from '../models/reader-edition.ts';
import type {Reel} from '../models/reel.ts';
import {executeQuery} from '../services/content.ts';

export abstract class ContentAdapter {
	abstract readonly locale: string;
	abstract readonly timezone: string;
	singleItemSourceId: string | undefined;
	abstract getSectionForPrintItem(printPage: Page, item: Item, parser: LayoutParser): string;
	abstract getStoryForPrintItem(printPage: Page, item: Item, parser: LayoutParser): string;
	abstract loadItem(
		article: Article,
		groupBy: string | null,
		sortOrder: number | undefined,
		store: Store,
		sourceId?: string
	): Promise<Article | null>;
	abstract formatListItems(
		items: (ListItemData | Reel)[],
		groupBy: string | null,
		date: string | null,
		store: Store,
		sourceId?: string,
		timezone?: string
	): ReaderItem[];
	abstract createEditionBrief(
		data: DigitalEditionItemData,
		store: Store,
		articleIdsInEditiond: string[],
		editionTitle: string
	): Article;

	abstract getListQueryParams(publishDate: string, tag: string, articleId: string): Record<string, number | string>;

	abstract loadSingleItem(articleId: string, sourceId: string | undefined, store: Store): Promise<Article>;

	loadSingleItemFromFixture(_data: string, _store: Store): Article {
		throw new Error('loadSingleItemFromFixture not implemented for this adapter');
	}

	async ensurePublishedAt(_item: ListItemData | Reel): Promise<void> {}

	async loadArticleList(
		edition: ContentSourceEdition,
		_store: Store
	): Promise<(ContentQueryResultItem | {data: Reel})[]> {
		const params = this.getListQueryParams(edition.publishDate, edition.tag, edition.articleId);

		const articlesResponse = await executeQuery(edition.sourceId, edition.queryId, params);

		if (articlesResponse.status !== 200) {
			throw new LoadResourceError('Error loading content query source', articlesResponse);
		}

		return articlesResponse.body.items;
	}

	loadRelatedArticles(_article: Article, _sourceId?: string): Promise<RelatedArticle[]> {
		return Promise.resolve([]);
	}

	formatReel(_reel: PlaylistItem): ReelData {
		throw new Error('Format reel not implemented');
	}

	async loadPlaylist(_id: string): Promise<PlaylistResponse> {
		throw new Error('Load playlist not implemented');
	}

	async loadArticleById(_id: string, _store: Store): Promise<Article | null> {
		throw new Error('Load article by id not implemented');
	}
}
